import React from 'react'
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom'
import { COMPANY_ROUTES, ROUTES } from 'client-portal/constants/routes'
import { CompanyDetails } from 'client-portal/routes/CompanyDetails'
import { CompanyGroups } from 'client-portal/routes/CompanyGroups'
import { CompanyGroupDetails } from 'client-portal/routes/CompanyGroupsDetails'
import { CompanyTestSessions } from 'client-portal/routes/CompanyTestSessions'
import { MemberDetails } from 'client-portal/routes/MemberDetails'
import { CompanyTestSessionDetails } from 'client-portal/routes/CompanyTestSessionDetails'
import { RequestGroupTestSession } from 'client-portal/routes/RequestGroupTestSession'
import { CompanyEventClearance } from 'client-portal/routes/CompanyEventClearance'
import { EventSession } from 'client-portal/routes/Events/EventsPage'
import { Analytics } from 'client-portal/routes/Analytics'
import {
  useAuth,
  UserRole,
  useCompanyMember,
  CompanyIsAllowed,
  GroupMemberRole,
  CompanyMemberRole,
} from '@modmd/data'
import EventDetail from 'sharedComponents/EventsDetail/EventsDetail'

interface RouterProps {
  companyId: string
}

export const CompanyRoutes: React.VFC<RouteComponentProps<RouterProps>> = ({ match }) => {
  const { companyId } = match.params
  const auth = useAuth()
  const { hasRoles, getUserGroupsWithRole, getUserCompaniesWithRole } = useAuth()
  const isSuperAdmin = hasRoles([UserRole.SUPER_ADMIN])
  const isCLS = hasRoles([UserRole.CLS])
  const { data: isCompanyAllowed } = useCompanyMember({
    userId: auth.data.User.id,
  }) as CompanyIsAllowed
  const { hasGroupsWithRole: isGroupManager } = getUserGroupsWithRole({
    companyIds: [companyId],
    roles: [GroupMemberRole.Manager],
  })
  const { hasCompaniesWithRole: isCompanyManagerOrOwner } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
  })

  return (
    <Switch>
      <Route exact path={`${match.path}/${COMPANY_ROUTES.GROUPS}`} component={CompanyGroups} />
      <Route
        exact
        path={`${match.path}/${COMPANY_ROUTES.GROUPS}/:groupId`}
        component={CompanyGroupDetails}
      />
      <Route
        exact
        path={`${match.path}/${COMPANY_ROUTES.MEMBERS}/:memberId`}
        component={MemberDetails}
      />
      <Route
        exact
        path={`${match.path}/${COMPANY_ROUTES.TEST_SESSIONS}`}
        component={CompanyTestSessions}
      />
      <Route exact path={`${match.path}/${COMPANY_ROUTES.EVENTS}`} component={EventSession} />
      <Route
        exact
        path={`${match.path}/${COMPANY_ROUTES.EVENTS}/:eventId`}
        component={EventDetail}
      />
      <Route
        exact
        path={`${match.path}/${COMPANY_ROUTES.TEST_SESSIONS}/:testSessionId`}
        component={CompanyTestSessionDetails}
      />
      <Route exact path={`${match.path}/${COMPANY_ROUTES.DETAILS}`} component={CompanyDetails} />
      <Route
        exact
        path={`${match.path}/${ROUTES.REQUEST_TEST_SESSION}/:groupId`}
        component={RequestGroupTestSession}
      />
      {(isSuperAdmin || isCompanyAllowed?.companyMember || isCLS) && (
        <Route
          exact
          path={`${match.path}/${COMPANY_ROUTES.EVENT_CLEARANCE}`}
          component={CompanyEventClearance}
        />
      )}
      {(isGroupManager || isCompanyManagerOrOwner) && (
        <Route exact path={`${match.path}/${COMPANY_ROUTES.ANALYTICS}`} component={Analytics} />
      )}
      <Redirect to={`/${ROUTES.PERSONAL}`} />
    </Switch>
  )
}
